/**
 * Turns a full dispensary `address` string (often "street, city, ST zip, USA" or
 * "street, city, ON postal, Canada") into a short display line for order history:
 * street, city, region (comma-separated) — no postal/ZIP, no country suffix.
 */

/** Trailing country token so comma-split does not treat it as its own segment. */
const TRAILING_COUNTRY = /,?\s*(USA|United States|Canada|CAN)\s*$/i;

/** Last comma-segment: US state + optional ZIP, e.g. "MA 02740" or "OR".
 * Will also catch any two-letter region codes without a postal code, e.g. "ON". */
const US_STATE_AND_OPTIONAL_ZIP = /^([A-Za-z]{2})\s*(\d{5}(-\d{4})?)?$/;

/** Last comma-segment: province + Canadian postal, e.g. "ON P3B 4K6". */
const CA_PROVINCE_AND_POSTAL = /^([A-Za-z]{2})\s+([A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d)\s*$/i;

/** Removes a trailing US ZIP (5 or 5+4) from the end of a segment, e.g. "Something 97201" → "Something". */
function stripTrailingUsZip(s: string): string {
  return s.replace(/\s+\d{5}(-\d{4})?\s*$/g, '').trim();
}

function splitCommaSegments(address: string): string[] {
  return address
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * When the last segment was recognized as region (state/province) + postal, `segments` is still the
 * full comma list including that last segment. Builds the short display line from the leading parts.
 */
function formatLeadingPartsWithRegion(
  segments: string[],
  region: string,
  formatMultiLeadingParts: (leadingParts: string[]) => string
): string {
  const leadingParts = segments.slice(0, -1);

  if (leadingParts.length === 0) {
    return region;
  }

  if (leadingParts.length === 1) {
    return `${stripTrailingUsZip(leadingParts[0] ?? '')}, ${region}`;
  }

  return formatMultiLeadingParts(leadingParts);
}

/**
 * Parses common comma-separated dispensary addresses for US and Canada and drops ZIP + country.
 *
 * @example
 * // Typical stored address → order list subtitle
 * formatDispensaryAddressWithoutZipOrCountry('95 Coggeshall St, New Bedford, MA 02740, USA')
 * // → '95 Coggeshall St, New Bedford, MA'
 *
 * @example
 * formatDispensaryAddressWithoutZipOrCountry('1199 NW Wall St, Bend, OR 97701, USA')
 * // → '1199 NW Wall St, Bend, OR'
 */
export function formatDispensaryAddressWithoutZipOrCountry(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) {
    return '';
  }

  const addressWithoutCountry = trimmed.replace(TRAILING_COUNTRY, '').trim();
  if (!addressWithoutCountry) {
    return '';
  }

  const segments = splitCommaSegments(addressWithoutCountry);

  // Single chunk after country removal: only try to peel a trailing ZIP off the end.
  // e.g. "Something weird 97201" → "Something weird"
  if (segments.length === 1) {
    return stripTrailingUsZip(segments[0] ?? '');
  }

  const lastSegment = segments[segments.length - 1] ?? '';

  const usStateCode = US_STATE_AND_OPTIONAL_ZIP.exec(lastSegment)?.[1];
  if (usStateCode) {
    const state = usStateCode.toUpperCase();
    return formatLeadingPartsWithRegion(segments, state, (leadingParts) => `${leadingParts.join(', ')}, ${state}`);
  }

  const provinceCode = CA_PROVINCE_AND_POSTAL.exec(lastSegment)?.[1];
  if (provinceCode) {
    const province = provinceCode.toUpperCase();
    return formatLeadingPartsWithRegion(
      segments,
      province,
      (leadingParts) => `${leadingParts.join(', ')}, ${province}`
    );
  }

  return stripTrailingUsZip(addressWithoutCountry);
}
